// EditPicksPage.jsx — Phase-9 lean edit mode.
//
// Routes:
//   /my/path/edit       → edit the 5 primary picks
//   /my/discovery/edit  → edit the 2 discovery picks (and optionally switch path)
//
// Reuses the existing PathSelectionFlow (one source of truth per path) but
// pre-loads the user's current picks so they can swap freely without losing
// their slots. Saving calls the lean /api/my/*-previews/update endpoints.

import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Loader2, ArrowLeft, Save, Sparkles, Compass, AlertTriangle,
  Star, Briefcase, GraduationCap, Landmark, Rocket, Layers,
  Sparkles as SparklesIcon, RefreshCcw,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import Navbar from '../components/Navbar';
import PathSelectionFlow from '../components/PathSelectionFlow';
import { useAuth } from '../lib/auth';
import {
  fetchPathCatalogue, fetchMyState, fetchMyPrimaryPreviews,
  fetchMyDiscoveryPreviews, updatePrimaryPreviews, updateDiscoveryPreviews,
  updateDiscoveryPath,
} from '../lib/pathsApi';

const ICONS = {
  'graduation-cap': GraduationCap, 'layers': Layers, 'briefcase': Briefcase,
  'sparkles': SparklesIcon, 'landmark': Landmark, 'rocket': Rocket,
};
const COLOR = {
  amber:   'from-amber-500 to-amber-700',
  teal:    'from-teal-500 to-teal-700',
  blue:    'from-blue-500 to-blue-700',
  purple:  'from-purple-500 to-purple-700',
  emerald: 'from-emerald-500 to-emerald-700',
  rose:    'from-rose-500 to-rose-700',
};

const MAX = { primary: 5, discovery: 2 };

export default function EditPicksPage({ mode = 'primary' }) {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const [loading, setLoading] = useState(true);
  const [catalogue, setCatalogue] = useState([]);
  const [state, setState] = useState(null);
  const [selections, setSelections] = useState([]);    // [{item_type, item_id, item_name_verbatim, kind}]
  const [activePath, setActivePath] = useState(null);  // path key to edit inside
  const [showPathSwitch, setShowPathSwitch] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // ─── Initial load ───
  useEffect(() => {
    if (user === null) return;
    if (user === false) { navigate('/signin', { replace: true }); return; }
    // After 10th / Inter = quiz-only. No previews, no picks UI on this path.
    if (user.primary_path === 'after_10th_inter' && mode === 'primary') {
      navigate('/career-clarity', { replace: true });
      return;
    }
    // Inner Specializations users → bounce to /specializations picker.
    if (user.primary_path === 'specializations' && mode === 'primary') {
      navigate('/specializations', { replace: true });
      return;
    }
    if (user.primary_path === 'job_roles' && mode === 'primary') {
      navigate('/job-roles', { replace: true });
      return;
    }
    (async () => {
      try {
        const [cat, st] = await Promise.all([fetchPathCatalogue(), fetchMyState()]);
        setCatalogue(cat);
        setState(st);
        const pathKey = mode === 'primary' ? st.primary_path : st.discovery_path;
        setActivePath(pathKey);
        // Pre-load existing picks as selections (only items inside the active path)
        if (pathKey) {
          const previewResp = mode === 'primary'
            ? await fetchMyPrimaryPreviews()
            : await fetchMyDiscoveryPreviews();
          const pre = (previewResp.previews || []).map((p, idx) => ({
            item_type: p.item_type || 'preview',
            item_id: String(p.catalog_number || p.id || p.slug),
            item_name_verbatim: p.role_name || p.title || p.name || `Preview ${idx + 1}`,
            kind: mode === 'primary' && idx === 0 ? 'primary' : 'exploring',
          }));
          setSelections(pre);
        }
      } catch (err) {
        setError(err?.response?.data?.detail || err.message || 'Could not load.');
      } finally { setLoading(false); }
    })();
  }, [user, mode]);

  const handleSave = async () => {
    setError('');
    setSaving(true);
    try {
      const ids = selections.map((s) => String(s.item_id));
      if (mode === 'primary') {
        await updatePrimaryPreviews(ids);
        await refresh();
        navigate('/my/path');
      } else {
        await updateDiscoveryPreviews(ids);
        await refresh();
        navigate('/my/discovery');
      }
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Could not save.');
    } finally { setSaving(false); }
  };

  const handleSwitchDiscoveryPath = async (newKey) => {
    setError('');
    setSaving(true);
    try {
      await updateDiscoveryPath(newKey);
      await refresh();
      // Reload state with cleared selections
      const [st] = await Promise.all([fetchMyState()]);
      setState(st);
      setActivePath(newKey);
      setSelections([]);
      setShowPathSwitch(false);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Could not switch path.');
    } finally { setSaving(false); }
  };

  if (loading || user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-50 to-blue-50">
        <Loader2 className="w-10 h-10 animate-spin text-teal-600" />
      </div>
    );
  }

  const max = MAX[mode];
  const limitReached = selections.length >= max;
  const meta = catalogue.find((p) => p.key === activePath);
  const Icon = meta ? (ICONS[meta.icon] || Briefcase) : (mode === 'primary' ? Star : Compass);
  const gradient = meta ? COLOR[meta.color] : (mode === 'primary' ? COLOR.teal : COLOR.purple);

  // ─── No path yet ───
  if (!activePath) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-blue-50">
        <Navbar />
        <div className="pt-28 pb-16 px-4">
          <div className="max-w-2xl mx-auto bg-white border border-gray-200 rounded-3xl p-8 text-center" data-testid={`edit-${mode}-no-path`}>
            <h2 className="text-lg font-bold text-gray-900 mb-2">
              {mode === 'primary' ? 'No primary path yet' : 'No discovery path yet'}
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              {mode === 'primary'
                ? 'Pick a primary path during signup first.'
                : 'Choose a discovery path from your dashboard before editing picks.'}
            </p>
            <Button asChild className="btn-primary">
              <Link to="/dashboard">Back to dashboard</Link>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Discovery path-switch UI ───
  const discoveryCandidates = catalogue.filter(
    (p) => p.key !== state?.primary_path && p.key !== activePath,
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-blue-50">
      <Navbar />
      <div className="pt-28 pb-16 px-4">
        <div className="max-w-4xl mx-auto">

          {/* Top bar */}
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <Link
              to={mode === 'primary' ? '/my/path' : '/my/discovery'}
              className="inline-flex items-center text-sm font-semibold text-gray-700 hover:text-teal-700"
              data-testid="back-link"
            >
              <ArrowLeft size={14} className="mr-1.5" /> Back
            </Link>
            <Button
              onClick={handleSave}
              disabled={saving || selections.length === 0}
              className="btn-primary"
              data-testid="save-picks-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save my {selections.length} pick{selections.length === 1 ? '' : 's'}
            </Button>
          </div>

          {/* Hero */}
          <div className={`bg-gradient-to-br ${gradient} rounded-3xl p-6 md:p-8 text-white shadow-xl mb-6`} data-testid={`edit-${mode}-hero`}>
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center flex-shrink-0">
                <Icon className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <div className="text-white/80 text-[11px] uppercase tracking-wider mb-1 flex items-center gap-1">
                  {mode === 'primary'
                    ? <><Sparkles size={11} /> Edit your primary picks</>
                    : <><Compass size={11} /> Edit your discovery picks</>}
                </div>
                <h1 className="text-2xl md:text-3xl font-bold leading-tight">{meta?.label || activePath}</h1>
                <p className="text-white/90 text-sm mt-1">
                  Pick up to <strong>{max}</strong>. Swap freely — credits release the moment you remove a pick.
                </p>
                <div className="mt-3 inline-block bg-white/15 backdrop-blur-sm rounded-full px-3 py-1 text-xs font-semibold" data-testid="edit-counter">
                  Selected: <strong>{selections.length}/{max}</strong> · Credits remaining: <strong>{max - selections.length}</strong>
                </div>
              </div>
            </div>
            {mode === 'discovery' && discoveryCandidates.length > 0 && (
              <div className="mt-5">
                <button
                  type="button"
                  onClick={() => setShowPathSwitch((v) => !v)}
                  className="inline-flex items-center gap-1.5 bg-white/15 hover:bg-white/25 backdrop-blur-sm rounded-full px-3 py-1.5 text-xs font-semibold transition"
                  data-testid="switch-discovery-path-btn"
                >
                  <RefreshCcw size={11} /> {showPathSwitch ? 'Cancel switch' : 'Switch discovery path'}
                </button>
                {showPathSwitch && (
                  <div className="mt-3 bg-white/10 backdrop-blur-sm rounded-2xl p-4 space-y-2" data-testid="discovery-switch-panel">
                    <div className="text-[11px] uppercase tracking-wider text-white/80 mb-2">
                      <AlertTriangle size={11} className="inline mr-1" /> Switching replaces your current 2 picks
                    </div>
                    {discoveryCandidates.map((p) => (
                      <button
                        key={p.key}
                        type="button"
                        onClick={() => handleSwitchDiscoveryPath(p.key)}
                        disabled={saving}
                        className="w-full text-left bg-white text-gray-900 rounded-xl px-4 py-3 hover:shadow-md transition disabled:opacity-50"
                        data-testid={`switch-to-${p.key}`}
                      >
                        <div className="font-bold text-sm">{p.label}</div>
                        <div className="text-xs text-gray-600 mt-0.5">{p.tagline}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {limitReached && (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-3 text-xs text-amber-800 mb-4 flex items-start gap-2" data-testid="limit-banner">
              <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
              <div>You&apos;ve filled all {max} slots. Remove a pick to add a different one — your credit comes back instantly.</div>
            </div>
          )}

          {error && (
            <div className="bg-rose-50 border border-rose-200 text-rose-700 text-sm rounded-2xl p-3 mb-4" data-testid="edit-error">
              {error}
            </div>
          )}

          {/* Reused selection flow */}
          <div className="bg-white border border-gray-200 rounded-3xl p-6 md:p-8 shadow-sm">
            <PathSelectionFlow
              pathKey={activePath}
              selections={selections}
              maxSelections={max}
              onSelectionsChange={(next) => setSelections(next.slice(0, max))}
            />
          </div>

          {/* Sticky save bar (mobile-friendly) */}
          <div className="mt-6 flex items-center justify-between gap-3 flex-wrap">
            <div className="text-xs text-gray-600">
              {mode === 'primary'
                ? 'Your 5 primary picks are the only previews loaded into your dashboard.'
                : 'Your 2 discovery picks are the only previews loaded into your discovery window.'}
            </div>
            <Button
              onClick={handleSave}
              disabled={saving || selections.length === 0}
              className="btn-primary"
              data-testid="save-picks-btn-bottom"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save & return
            </Button>
          </div>

        </div>
      </div>
    </div>
  );
}
